import React, { useCallback, useMemo } from 'react';
import Animated from 'react-native-reanimated';
import LinearGradient from 'utils/packages/linearGradient';
import { FlashList } from '@shopify/flash-list';
import IconPlus from 'design-system/icons/IconPlus';
import { SettingsItem } from 'design-system/SettingsItem';
import FlatListItemCard from 'design-system/FlatListItemCard';
import { rem } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import useColorScheme from 'hooks/useColorScheme';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import { selectOrderedParentCategories, selectNormalizedCategories, } from 'reducers/selectors';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import InfoCard from 'design-system/InfoCard';
import Text from 'design-system/Text';
import { formatPlural } from 'utils';
import { isWeb } from '../../../../constants';
import EditCategoryListVerticalRow from './EditCategoryVerticalListRow';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const keyExtractor = (item) => item.id;
const ManageCategoriesList = () => {
    const customCategories = useAppSelector(selectOrderedParentCategories);
    const normalizedCategories = useAppSelector(selectNormalizedCategories);
    const data = useMemo(() => customCategories.map((category) => ({
        ...category,
        subcategoriesLength: normalizedCategories[category.id]?.subCategoryIds?.length || 0,
    })), [customCategories, normalizedCategories]);
    const styles = useStyles(styleSet);
    const onPressCategory = useRequiredBenefitCallbackWithSpaces(useCallback((category) => {
        navigation.navigateDeprecated('CustomCategoryEdit', { category });
    }, []), 'customCategories');
    const renderItem = useCallback(({ item, index }) => (<FlatListItemCard index={index + 1} itemsLength={data.length + 1} style={styles.listItem}>
        <EditCategoryListVerticalRow category={item} description={item.subcategoriesLength
            ? `${item.subcategoriesLength} ${formatPlural('subcategory', 'subcategories', item.subcategoriesLength)}`
            : ''} onPress={onPressCategory} showEdit/>
      </FlatListItemCard>), [data.length, onPressCategory, styles.listItem]);
    const colors = useColors();
    const navigation = useMainStackNavigation();
    const ListHeaderComponent = useMemo(() => {
        const content = (<SettingsItem title="Add new category" style={!data.length && styles.addButton} disabled={!data.length} onPress={() => navigation.navigateDeprecated('CreateCustomCategory')} renderIcon={() => (<LinearGradient colors={[colors.background.dark, colors.background.dark]} style={styles.addIcon}>
            <IconPlus color={colors.elements.brand}/>
          </LinearGradient>)}/>);
        if (!data.length) {
            return (<TouchableBounceCard onDark onPress={() => navigation.navigateDeprecated('CreateCustomCategory')} noMarginTop noMarginBottom>
          {content}
        </TouchableBounceCard>);
        }
        return (<FlatListItemCard index={0} itemsLength={data.length + 1} style={styles.listItem}>
        {content}
      </FlatListItemCard>);
    }, [
        colors.background.dark,
        colors.elements.brand,
        data.length,
        styles.addButton,
        styles.addIcon,
        styles.listItem,
    ]);
    const ListFooterComponent = useMemo(() => (<InfoCard style={styles.infoCard}>
        <Text TextThin-14 Secondary>
          Default categories can’t be edited, but you can create unlimited
          custom categories.
        </Text>
      </InfoCard>), [styles.infoCard]);
    const colorScheme = useColorScheme();
    const baseProps = {
        scrollEnabled: false,
        data,
        renderItem,
        keyExtractor,
        contentContainerStyle: styles.container,
        ListHeaderComponent,
        ListFooterComponent,
    };
    if (isWeb) {
        return <Animated.FlatList {...baseProps}/>;
    }
    return <AnimatedFlashList {...baseProps} extraData={colorScheme}/>;
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        paddingTop: rem(16),
    },
    listItem: {
        backgroundColor: colors.cards.onDark,
    },
    addIcon: {
        width: rem(44),
        height: rem(44),
        borderRadius: rem(44),
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: rem(16),
    },
    infoCard: {
        marginTop: rem(16),
    },
    addButton: {
        marginHorizontal: rem(16),
    },
}));
export default ManageCategoriesList;
